import React from "react";
import { Link } from "react-router-dom";
import EventSourceChip from "@/components/events/EventSourceChip";
import { MapPin, Calendar, User } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/**
 * Legacy-parity event card (founder screenshot): poster with orange
 * day/month badge + city tag, then title, address, organiser, date and
 * an orange "View Event" button. Shared by the homepage Events row and
 * the /events grid.
 */

function dateParts(iso) {
  if (!iso) return { day: "", month: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: "", month: "" };
  return {
    day: String(d.getDate()).padStart(2, "0"),
    month: d.toLocaleDateString("en-NZ", { month: "short" }).toUpperCase(),
  };
}

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-NZ", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function EventCard({ event: e, testId }) {
  const { t } = useI18n(); // Iter 704 — card follows the site language
  const { day, month } = dateParts(e.starts_at);
  const address = e.address || e.venue || "";
  return (
    <Link
      to={`/events/${e.id}`}
      data-testid={testId || `event-card-${e.id}`}
      className="group flex flex-col rounded-xl bg-white shadow-card hover:shadow-card-hover overflow-hidden card-lift"
    >
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        {e.image_url && (
          <img src={e.image_url} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        )}
        {day && (
          <div className="absolute top-3 left-3 rounded-lg bg-[color:var(--brand-orange)] text-white shadow-card px-2.5 py-1 text-center leading-none">
            <div className="font-display text-lg font-black">{day}</div>
            <div className="text-[10px] font-bold uppercase">{month}</div>
          </div>
        )}
        {(e.city || e.region) && (
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-900/70 to-transparent px-3 pt-6 pb-2">
            <span className="inline-flex items-center gap-1 text-white text-xs font-semibold">
              <MapPin className="w-3 h-3" /> {e.city || e.region}
            </span>
          </div>
        )}
        <div className="absolute top-3 right-3">
          <EventSourceChip source={e.source} priceLabel={e.price_label} />
        </div>
        {/* Iter 656a — "★ AllSale Sponsored" ribbon across the image bottom. */}
        {e.sponsorship?.is_sponsored && (
          <div
            data-testid={`cev-prize-ribbon-${e.id}`}
            className="absolute bottom-0 inset-x-0 bg-[#FF6200] text-white text-[12px] font-extrabold px-3 py-1.5 truncate"
          >
            ★ ALLSALE SPONSORED ·{" "}
            {e.sponsorship.status === "drawn" ? "Drawn" : `Win ${e.sponsorship.prize_label || "a prize"}`}
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 p-4">
        <h3 className="font-display font-bold text-slate-900 clamp-1 group-hover:text-[color:var(--brand-blue)] transition-colors">
          {e.title}
        </h3>
        <ul className="mt-2 space-y-1.5 text-xs text-slate-500">
          {address && (
            <li className="flex items-start gap-1.5">
              <MapPin className="w-3.5 h-3.5 shrink-0 mt-px text-[color:var(--brand-orange)]" />
              <span className="clamp-2">{address}</span>
            </li>
          )}
          {e.organizer_name && (
            <li className="flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-orange)]" />
              <span className="clamp-1">{e.organizer_name}</span>
            </li>
          )}
          {e.starts_at && (
            <li className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-orange)]" />
              {shortDate(e.starts_at)}
            </li>
          )}
        </ul>
        <span data-testid={`event-view-${e.id}`} className="mt-auto pt-3 block">
          <span className="block w-full rounded-lg bg-[color:var(--brand-orange)] group-hover:bg-[color:var(--brand-orange-hover)] text-white text-center text-sm font-bold py-2 transition-colors">
            {t("View Event")}
          </span>
        </span>
      </div>
    </Link>
  );
}
